import { isApiFormError, isFieldErrorResponse, isNetworkError } from 'api';
import { addError } from 'monitor';
import { createElement, useCallback } from 'react';
import {
  type FieldValues,
  type Path,
  type UseFormProps,
  type UseFormReturn,
  useForm as useReactHookForm,
} from 'react-hook-form';
import { toast } from 'react-toastify';

import { scrollToFirstError } from '../../utils/scrollToFirstError/scrollToFirstError';

const openErrorNotification = ({ description, message }: { description?: string; message: string }) => {
  toast.error(
    createElement(
      'div',
      null,
      createElement('div', null, message),
      description ? createElement('div', null, description) : null
    ),
    {
      hideProgressBar: true,
    }
  );
};

export type UseFormWithApiErrorsReturn<
  TFieldValues extends FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
> = UseFormReturn<TFieldValues, TContext, TTransformedValues> & {
  setApiErrors: <TError = unknown>(error: TError, options?: SetApiErrorsOptions) => void;
};

export type SetApiErrorsOptions = {
  /**
   * Custom error message to show in the notification for non-field errors.
   * Defaults to 'An error occurred'
   */
  errorMessage?: string;
  /**
   * Whether to scroll to the first error field after setting errors.
   * Defaults to true
   */
  scrollToError?: boolean;
  /**
   * Whether to show a notification for non-field errors.
   * Defaults to true
   */
  showNotification?: boolean;
};

/**
 * Custom hook that wraps react-hook-form's useForm and provides automatic API error mapping.
 *
 * Features:
 * - Automatically maps field errors from API responses to form fields
 * - Shows notifications for non-field errors
 * - Auto-scrolls to the first error field
 * - Handles fallback for non-field error responses (e.g., 500 errors, network errors)
 *
 * @example
 * ```tsx
 * const form = useFormWithApiErrors({ resolver: yupResolver(schema) });
 * const { mutate } = useMutation({
 *   onError(error) {
 *     form.setApiErrors(error);
 *   }
 * });
 * ```
 */
export const useFormWithApiErrors = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  props?: UseFormProps<TFieldValues, TContext, TTransformedValues>
): UseFormWithApiErrorsReturn<TFieldValues, TContext, TTransformedValues> => {
  const form = useReactHookForm<TFieldValues, TContext, TTransformedValues>(props);

  const setApiErrors = useCallback(
    <TError = unknown>(error: TError, options?: SetApiErrorsOptions) => {
      const { errorMessage, scrollToError = true, showNotification = true } = options ?? {};
      const fallbackErrorMessage = errorMessage ?? 'An error occurred';

      // Check if this is a field error response
      if (isFieldErrorResponse(error)) {
        const errorData = error.response?.data;
        if (!errorData) {
          return;
        }

        // Set field errors
        Object.keys(errorData).forEach((field) => {
          if (field === 'non_field_errors') {
            return;
          }

          // Type guard: only set error if field exists in form
          // This check ensures we don't try to set errors on fields that don't exist in the form schema
          if (field in form.getValues()) {
            form.setError(field as Path<TFieldValues>, { message: errorData[field] });
          } else {
            addError(Error('Field error not part of form values'), {
              error,
              field,
              formValues: form.getValues(),
            });
          }
        });

        // Handle non-field errors
        if (errorData.non_field_errors && showNotification) {
          openErrorNotification({
            description: Array.isArray(errorData.non_field_errors)
              ? errorData.non_field_errors[0]
              : errorData.non_field_errors,

            message: fallbackErrorMessage,
          });
        }

        // Scroll to first error
        if (scrollToError) {
          requestAnimationFrame(() => {
            scrollToFirstError();
          });
        }
        return;
      }

      // Fallback for non-field error responses (e.g., 500 errors, network errors)
      if (isApiFormError(error)) {
        if (showNotification) {
          openErrorNotification({
            message: fallbackErrorMessage,
          });
        }
        return;
      }

      if (isNetworkError(error)) {
        if (showNotification) {
          openErrorNotification({
            description: "We're having trouble connecting. Check your network and try again.",
            message: fallbackErrorMessage,
          });
        }
        return;
      }

      // Final fallback for any other error type (e.g., non-Axios errors)
      if (showNotification) {
        openErrorNotification({
          message: fallbackErrorMessage,
        });
      }

      // Log unexpected error types for monitoring
      addError(Error('Unexpected error type in setApiErrors'), {
        error,
        errorType: typeof error,
      });
    },
    [form]
  );

  return {
    ...form,
    setApiErrors,
  };
};
